import { makeAutoObservable, runInAction, toJS } from 'mobx';
import axios from "axios";
import RootStore from './RootStore';
import CardStore from './CardStore';
import { NODE_URL_MAP } from 'src/constants';

import {tokenInfo, verifiedCollections} from '../constants/common';

const MAINNET_ADDRESS = NODE_URL_MAP.W;
const MAINNET_CREATE_DAPP = '3PFQjjDMiZKQZdu5JqTHD7HwgSXyp9Rw9By';
const MAINNET_MARKET_DAPP = '3PATXDVE53rKJtkwXYV3tvuLVS2fDtGtKCc';
const MAINNET_DAO = '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh';

const MAINNET_SIGNARTWRAP_DAPP = '3PPgeYTWMRZ7S7jSLpnVuKKB9r6XuDk3ndC';
const MAINNET_DUCKWAPP_DAPP = '3P3pDosq4GCwfJkvq4yqKvvoTwmoqc9qPmo';

// waves ducks issue contracts
const MAINNET_DUCKS_INCUBATOR_DAPP = '3PEktVux2RhchSN63DsDo4b4mz4QqzKSeDv';
const MAINNET_DUCKS_BREEDER_DAPP = '3PDVuU45H7Eh5dmtNbnRNRStGwULA7NY6Hb';
const MAINNET_FARMING_DAPP = '3PAETTtuW7aSiyKtn9GuML3RgtV1xdq1mQW';

const limit = 1000;
const millisecondsPerDay = 1000 * 60 * 60 * 24;

interface INftAsset {
    assetId: string;
    issuer: string;
    name: string;
    description: string;
    issueTimestamp: number;
    startSaleAt: number;
}
interface IAssetsNftResponse extends Array<INftAsset> {};

interface IAddressData {
    key: string;
    type: string;
    value: string|number;
}
interface IAddressDataResponse extends Array<IAddressData> {};

type SortingDirection = 'asc'|'desc';

class MarketStore {
    rootStore: RootStore;

    // original
    items: CardStore[] = [];

    // changed by filter, sorting
    filteredItems: CardStore[] = [];

    rawItems: any[] = [];

    sortingField: keyof CardStore = 'startSaleAt';
    sortingDirection: SortingDirection = 'desc';

    searchValue: string = '';

    filter: {
        collection: string,
        onlyEagles: boolean
    } = {
        collection: 'all',
        onlyEagles: false
    }

    isLoading: boolean = true;

    daoMember: boolean = false;

    blocksHeight: number = 0;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this);
        this.rootStore = rootStore;

        this.loadItems();
        this.updateItems();
    }

    updateItems(): void {
        this.rootStore.requestQueue.addToQueue(() => {
            this.loadItems();
        });
    }

    async loadItems(): Promise<void> {
        this.isLoading = true;
        try {
            let dappNftsResult: IAssetsNftResponse = [];
            // hot fix
            let after;
            while(true) {
                const res: any = await axios.get<IAssetsNftResponse>(`${MAINNET_ADDRESS}/assets/nft/${MAINNET_MARKET_DAPP}/limit/1000`, {
                    params: after ? {
                        after
                    } : {}
                });
                dappNftsResult = [...dappNftsResult, ...res.data]
                if (res.data.length === limit) {
                    after = res.data[res.data.length-1].assetId;
                } else {
                    break;
                }
            }
            const issuerDappDataResult = await axios.get<IAddressDataResponse>(`${MAINNET_ADDRESS}/addresses/data/${MAINNET_CREATE_DAPP}`, {
                params: {
                    matches: '(nft_|collection_|group_)(.*)'
                }
            });
            const marketDappDataResult = await axios.get<IAddressDataResponse>(`${MAINNET_ADDRESS}/addresses/data/${MAINNET_MARKET_DAPP}`, {
                params: {
                    matches: '(nft_|auction_|address_)(.*)'
                }
            });
            const signArtWrapperDappDataResult = await axios.get<IAddressDataResponse>(`${MAINNET_ADDRESS}/addresses/data/${MAINNET_SIGNARTWRAP_DAPP}`, {
                params: {
                    matches: '(.*)_assetId'
                }
            });
            const ducksWrapperDappDataResult = await axios.get<IAddressDataResponse>(`${MAINNET_ADDRESS}/addresses/data/${MAINNET_DUCKWAPP_DAPP}`, {
                params: {
                    matches: `(.*)_duckId`
                }
            });

            const daoDappDataResult = await axios.get<IAddressDataResponse>(`${MAINNET_ADDRESS}/addresses/data/${MAINNET_DAO}`, {
                params: {
                    matches: `vote_(.*)_(confirmed|collectionId)|all_staked_addresses`
                }
            });

            const heightResult = await axios.get<{height: number}>(`${MAINNET_ADDRESS}/blocks/height`);
            this.blocksHeight = heightResult?.data.height || 0;

            let dappNfts: any = {};
            // set nfts from market dapp
            if (dappNftsResult?.length) {
                dappNftsResult.forEach((item) => {
                    dappNfts[item.assetId] = item;
                });

                // set data from issuer dapp
                issuerDappDataResult?.data.forEach((item) => {
                    const [name, assetId, key] = item.key.split('_');
                    if (name === 'nft') {
                        if (dappNfts[assetId]) {
                            if (key === 'issuer') {
                                dappNfts[assetId].creator = item.value as string;
                            } else {
                                dappNfts[assetId][key] = item.value;
                            }
                        }
                    } else if (name === 'group') {
                        if (dappNfts[assetId]) {
                            if (key === 'available') {
                                dappNfts[assetId].groupAvailable = item.value as number;
                            } else if (key === 'size') {
                                dappNfts[assetId].groupSize = item.value as number;
                            }
                        }
                    }
                });
                // set data from market dapp
                marketDappDataResult?.data.forEach((item, index, arr) => {
                    const [name, assetId, key] = item.key.split('_');
                    if (name === 'nft') {
                        if (dappNfts[assetId]) {
                            dappNfts[assetId][key] = item.value;
                        }
                    } else if (name === 'auction') {
                        if (dappNfts[assetId] && item.value) {
                            dappNfts[assetId].bids = (item.value as string).split(',').map((item) => {
                                const parsed = item.split('_');
                                return {
                                    key: item,
                                    owner: parsed[0],
                                    amount: parseInt(parsed[1], 10),
                                    usdnPrice: (parseInt(parsed[1], 10) / tokenInfo[parsed[2]].decimals) * (this.rootStore.usdnPrices[parsed[2]] || 1),
                                    usdtPrice: (parseInt(parsed[1], 10) / tokenInfo[parsed[2]].decimals) * (this.rootStore.usdtPrices[parsed[2]] || 1),
                                    assetId: parsed[2]
                                };
                            }).sort((a, b) => {
                                if (a.usdtPrice > b.usdtPrice ) return 1;
                                if (a.usdtPrice < b.usdtPrice) return -1;
                                return 0;
                            });
                        }
                    } else if (name === 'address') {
                        const [, address, , assetId, startSaleKey] = item.key.split('_');
                        if (dappNfts[assetId] && startSaleKey === 'startSaleAt') {
                            dappNfts[assetId].startSaleAt = item.value;
                        }
                    }
                });

                // get signart original id
                const wrappedOriginalIds: any = {};
                let regex = /signArtNft_|_assetId/ig;
                signArtWrapperDappDataResult.data.forEach((item) => {
                    const assetId = item.key.replaceAll(regex, "");
                    wrappedOriginalIds[assetId] = item.value;
                    if (dappNfts[assetId]) {
                        dappNfts[assetId].signArtWrapp = true;
                    }
                });

                // get ducks original id
                regex = /nft_|_duckId/ig;
                ducksWrapperDappDataResult.data.forEach((item) => {
                    const assetId = item.key.replaceAll(regex, "");
                    wrappedOriginalIds[assetId] = item.value;
                    if (dappNfts[assetId]) {
                        dappNfts[assetId].wavesDucksWrapp = true;
                    }
                });

                const identicals: any = {};
                const items: CardStore[] = [];
                const rawItems: any[] = [];
                Object.keys(dappNfts).forEach((key) => {
                    // find likes for wrapped
                    if (wrappedOriginalIds[key]) {
                        dappNfts[key].likes = marketDappDataResult?.data.find((n) => {
                            return n.key === `nft_${wrappedOriginalIds[key]}_likes`;
                        })?.value || '';
                        dappNfts[key].originalId = wrappedOriginalIds[key];
                    }

                    let imageID = ''
                    if (dappNfts[key].image.indexOf('https://ipfs.io/ipfs/') === 0) {
                        imageID = dappNfts[key].image.replace('https://ipfs.io/ipfs/', '').split('/')[0];
                    } else {
                        imageID = dappNfts[key].image.replace('https://', '');
                    }
                    const seller = dappNfts[key].seller;
                    const buyOutPrice = dappNfts[key].buyOutPrice;
                    const buyOutAssetId = dappNfts[key].buyOutAssetId;

                    let identicalId;
                    if (dappNfts[key].creator === '3P4m4beJ6p1pMPHqCQMAXEdquUuXJz72CMe') {
                        const name = dappNfts[key].name;
                        identicalId = `${name}_${seller}_${buyOutPrice}_${buyOutAssetId}`;
                    } else {
                        identicalId = `${imageID}_${seller}_${buyOutPrice}_${buyOutAssetId}`;
                    }

                    if(identicals[identicalId]) {
                        identicals[identicalId].items.push(key);
                        if (dappNfts[identicals[identicalId].assetId].startSaleAt < dappNfts[key].startSaleAt) {
                            dappNfts[identicals[identicalId].assetId].startSaleAt = dappNfts[key].startSaleAt;
                        }
                        return;
                    } else {
                        identicals[identicalId] = {
                            assetId: key,
                            items: [key],
                            startSaleAt: dappNfts[key].startSaleAt
                        };
                    }
                });

                const daoVotes: any = {};
                daoDappDataResult.data.forEach((item) => {
                    if (item.key.indexOf('vote_') >= 0) {
                        const [,voteId, param] = item.key.split('_');
                        if (!daoVotes[voteId]) {
                            daoVotes[voteId] = {voteId};
                        }
                        daoVotes[voteId][param] = item.value;
                    } else if (
                        item.key === 'all_staked_addresses' &&
                        this.rootStore.accountStore.address &&
                        (item.value as string).indexOf(this.rootStore.accountStore.address) >= 0
                    ) {
                        this.daoMember = true;
                    }
                });
                const verifedCollectionIds: string[] = [];
                Object.keys(daoVotes).forEach((voteId) => {
                    if (daoVotes[voteId].confirmed)
                        verifedCollectionIds.push(daoVotes[voteId].collectionId);
                });

                Object.keys(dappNfts).forEach((key) => {
                    let imageID = ''
                    if (dappNfts[key].image.indexOf('https://ipfs.io/ipfs/') === 0) {
                        imageID = dappNfts[key].image.replace('https://ipfs.io/ipfs/', '').split('/')[0];
                    } else {
                        imageID = dappNfts[key].image.replace('https://', '');
                    }
                    const seller = dappNfts[key].seller;
                    const buyOutPrice = dappNfts[key].buyOutPrice;
                    const buyOutAssetId = dappNfts[key].buyOutAssetId;

                    let identicalId;
                    if (dappNfts[key].creator === '3P4m4beJ6p1pMPHqCQMAXEdquUuXJz72CMe') {
                        const name = dappNfts[key].name;
                        identicalId = `${name}_${seller}_${buyOutPrice}_${buyOutAssetId}`;
                    } else {
                        identicalId = `${imageID}_${seller}_${buyOutPrice}_${buyOutAssetId}`;
                    }

                    rawItems.push({
                        assetId: key,
                        ...dappNfts[key]
                    });
                    if (identicals[identicalId] && identicals[identicalId].assetId !== key) {
                        return;
                    }
                    let verified = verifedCollectionIds.some((collectionId) => {
                        return dappNfts[key].data.indexOf(collectionId) >= 0;
                    });
                    if (!verified) {
                        verified = verifiedCollections.some((collectionId) => {
                            return dappNfts[key].data.indexOf(collectionId) >= 0;
                        });
                    }
                    items.push(new CardStore({
                        assetId: key,
                        identical: identicals[identicalId],
                        verified,
                        ...dappNfts[key]
                    }, this.rootStore));
                });

                // decorate ducks
                const ducks = items.filter(item => item.isDuck);
                const genotypes: any = ducks.map((item) => {
                    return {
                        assetId: item.originalId,
                        genotype: item.description.match(/DUCK-([A-Z]+-[A-Z]+)/)?.[1] as string
                    }
                });

                const duckInfoResult = await this.rootStore.walletStore.getDuckInfo(genotypes);
                ducks.forEach((duck) => {
                    if (duckInfoResult?.[duck.originalId]) {
                        duck.characteristics = duckInfoResult[duck.originalId].params;
                        duck.achievements = duckInfoResult[duck.originalId].achievements;
                    }
                });

                // TODO: передалать
                runInAction(() => {
                    if (JSON.stringify(toJS(this.rawItems)) !== JSON.stringify(rawItems)) {
                        this.items = items;
                        this.rawItems = rawItems;
    
                        this.find();
                    }
                });
            } else {
                this.items = [];
                this.filteredItems = [];
                this.rawItems = [];
            }
            this.isLoading = false;

        } catch(e) {
            console.error(e);
            this.isLoading = false;
        }
    }

    setCollectionFilter(collection: string): void {
        this.filter.collection = collection;
    }

    setSorting(field: keyof CardStore, direction: SortingDirection = 'asc'): void {
        this.sortingField = field;
        this.sortingDirection = direction;
    }

    setSortingByTime(direction?: SortingDirection): void {
        this.setSorting('startSaleAt', direction);
    }

    setSortingByPrice(direction?: SortingDirection): void {
        this.setSorting('usdtPrice', direction);
    }

    setSortingByBid(direction?: SortingDirection): void {
        this.setSorting('bids', direction);
    }

    setSortingByLike(direction?: SortingDirection): void {
        this.setSorting('likeCount', direction);
    }

    sortBy(): void {
        const field = this.sortingField;
        if (field !== 'bids') {
            this.filteredItems.sort((a, b) => {

                if ((field === 'startSaleAt' || field === 'likeCount') && (a.isPinned || b.isPinned)) {
                    if (a.isPinned && b.isPinned) return 0;
                    else if (a.isPinned) return -1;
                    else if (b.isPinned) return 1;
                }

                switch(this.sortingDirection) {
                    case 'desc':
                        if (a[field] < b[field] ) return 1;
                        if (a[field] > b[field]) return -1;
                        return 0;
                    default:
                        if (a[field] > b[field] ) return 1;
                        if (a[field] < b[field]) return -1;
                        return 0;
                }
            });
        } else if (field === 'bids') {
            this.filteredItems.sort((a, b) => {
                const a1 = a.bids?.[0]?.usdtPrice || (this.sortingDirection === 'desc' ? 0 : 9999999999);
                const b1 = b.bids?.[0]?.usdtPrice || (this.sortingDirection === 'desc' ? 0 : 9999999999);
                switch(this.sortingDirection) {
                    case 'desc':
                        if (a1 < b1) return 1;
                        if (a1 > b1) return -1;
                        return 0;
                    default:
                        if (a1 > b1) return 1;
                        if (a1 < b1) return -1;
                        return 0;
                }
            });
        }
    }

    find(): void {
        const collection = this.filter.collection || 'all';
        if (collection !== 'all') {
            if (collection === 'eagles') {
                this.filteredItems = this.items.filter(item => {
                    return item.description.indexOf('3PFTZA987iHHbP6UWVTbbrquNetcFSULqUP') >= 0 &&
                        (item.signArtWrapp || item.collectionId === '3PKVgd6ds1xervK6bPXbEHBqg8ibTcf5HZk_signArt')
                });
            } else {
                if (collection.indexOf('|')) {
                    this.filteredItems = this.items.filter(item => collection.split('|').indexOf(item.collectionId) >= 0);
                } else {
                    this.filteredItems = this.items.filter(item => item.collectionId === collection);
                }
            }
        } else {
            this.filteredItems = this.items;
        }
        if (this.searchValue) {
            const searchValue = this.searchValue.toLowerCase();
            this.filteredItems =
                this.filteredItems.filter((item) => {
                    const name = item.name.toLowerCase();
                    const collection = item.collectionName.toLowerCase();
                    return name.indexOf(searchValue) >= 0 ||
                        collection.indexOf(searchValue) >= 0 ||
                        (searchValue.indexOf('3P') === 0 && item.seller.indexOf(searchValue) >= 0)
                });
        }
        this.sortBy();
    }

    addItem(item: any): void {
        this.items.push(item);
    }

    deleteItem(id: string): void {
        // this.items = this.items.filter(item => item.id !== id);
    }
}

export default MarketStore;